import { useState } from "react";
import { motion } from "framer-motion";
import type { EventChoice, ScenarioReactiveLinePayload } from "@/api/types";
import { tokens } from "@/art/tokens";
import { AudioManager } from "@/engine/AudioManager";

interface ChoiceHintTone {
    accent: string;
    border: string;
    glow: string;
    background: string;
}

interface ChoiceReadabilityMeta {
    toneLabel: string;
    toneCopy: string;
    focusLabel: string;
    riskCopy: string | null;
    accent: string;
    background: string;
    endingBadges: Array<{ label: string; direction: "toward" | "away"; accent: string }>;
}

interface ChoiceAmbientParticle {
    top: string;
    left: string;
    size: number;
    duration: number;
    delay: number;
}

interface ChoiceSurfaceProfile {
    overlay: string;
    veil: string;
    particles: ChoiceAmbientParticle[];
    kicker: string;
}

interface ChoiceMenuProps {
    choices: EventChoice[];
    onPick: (choiceId: string) => void;
    disabled?: boolean;
    choiceHints?: Partial<Record<string, ScenarioReactiveLinePayload>>;
    resolveHintTone?: (line: ScenarioReactiveLinePayload) => ChoiceHintTone;
    eventTitle?: string | null;
    eventMessage?: string | null;
    eventLabel?: string | null;
}

/**
 * 2-3개 선택지 카드. 이벤트 사이 NarrativeBeat 재생 뒤 자연스럽게 이어진다.
 *
 * 4개 이상 선택지는 2줄로 wrap (2xN grid). 6개 초과는 3줄.
 * minHeight 는 1920x1080 기준 18vh clamp 로 잡아 작은 해상도에서도 균형 유지.
 */
export function ChoiceMenu({
    choices,
    onPick,
    disabled,
    choiceHints,
    resolveHintTone,
    eventTitle,
    eventMessage,
    eventLabel,
}: ChoiceMenuProps) {
    const [hoveredChoiceId, setHoveredChoiceId] = useState<string | null>(null);

    const handle = (id: string) => {
        if (disabled) return;
        AudioManager.sfx(tokens.audio.sfx.choiceCommit);
        onPick(id);
    };
    const handleHover = (id: string) => {
        if (disabled || hoveredChoiceId === id) return;
        setHoveredChoiceId(id);
        AudioManager.sfx(tokens.audio.sfx.buttonHover, 0.26);
    };

    const columns = choices.length <= 3
        ? 1
        : choices.length === 4
            ? 2
            : 2;
    const choiceMetas = choices.map((choice) => {
        const hint = choiceHints?.[choice.id];
        const hintTone = hint && resolveHintTone
            ? resolveHintTone(hint)
            : null;
        const readability = inferChoiceReadability(choice, hint);
        return {
            choice,
            hint,
            hintTone,
            readability,
            profile: buildChoiceSurfaceProfile(readability, hintTone),
        };
    });
    const situationAxes = Array.from(new Set(choiceMetas.map((entry) => entry.readability.focusLabel))).slice(0, 3);

    return (
        <div className="space-y-m">
            <div
                className="rounded-m p-m relative overflow-hidden"
                style={{
                    background: "linear-gradient(135deg, rgba(201,106,59,0.1), rgba(14,12,24,0.66))",
                    border: `1px solid ${tokens.color.ember}44`,
                    boxShadow: `inset 0 1px 0 ${tokens.color.ember}22`,
                }}
            >
                {/* 2026-04-23: 좌측 ember accent — Spirytale 언어 통일. */}
                <div
                    aria-hidden
                    className="absolute inset-y-0 left-0"
                    style={{
                        width: 2,
                        background: `linear-gradient(180deg, ${tokens.color.ember}FF 0%, ${tokens.color.ember}33 100%)`,
                        boxShadow: `0 0 10px ${tokens.color.ember}AA`,
                    }}
                />
                {/* 2026-04-25: "갈림길 · 지금의 선택" kicker 제거 — 이미 선택지 카드들이 노출돼 있으므로 자명. */}
                <div className="flex flex-wrap items-center justify-between gap-s pl-[6px]">
                    <div>
                        <div
                            className="font-display"
                            style={{
                                color: tokens.color.parchment,
                                fontSize: "clamp(20px, 1.6vw, 24px)",
                                letterSpacing: "0.04em",
                                textShadow: `0 0 14px ${tokens.color.ember}44, 0 1px 0 ${tokens.color.ink}`,
                            }}
                        >
                            지금 고를 판단
                        </div>
                    </div>
                    {eventLabel && (
                        <div
                            className="rounded-pill px-s py-[4px] font-display"
                            style={{
                                color: tokens.color.parchment,
                                fontSize: 13.5,
                                letterSpacing: tokens.type.letterSpacing.wide,
                                background: `${tokens.color.ink}88`,
                                border: `1px solid ${tokens.color.ember}44`,
                                boxShadow: `0 0 10px ${tokens.color.ember}22`,
                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                            }}
                        >
                            {eventLabel}
                        </div>
                    )}
                </div>
                {(eventTitle || eventMessage) && (
                    <div
                        className="mt-s rounded-s px-m py-s"
                        style={{
                            background: `${tokens.color.ink}88`,
                            border: `1px solid ${tokens.color.parchment}22`,
                        }}
                    >
                        {eventTitle && (
                            <div
                                className="font-display"
                                style={{
                                    color: tokens.color.parchment,
                                    fontSize: tokens.type.size.body,
                                    letterSpacing: tokens.type.letterSpacing.normal,
                                }}
                            >
                                {eventTitle}
                            </div>
                        )}
                        {eventMessage && (
                            <div
                                className="font-body mt-[6px]"
                                style={{
                                    color: tokens.color.parchment,
                                    fontSize: 15,
                                    lineHeight: 1.5,
                                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                }}
                            >
                                {eventMessage}
                            </div>
                        )}
                    </div>
                )}
                {situationAxes.length > 0 && (
                    <div className="mt-s flex flex-wrap gap-xs">
                        {situationAxes.map((axis) => (
                            <div
                                key={axis}
                                className="rounded-pill px-s py-[4px] font-display"
                                style={{
                                    color: tokens.color.parchment,
                                    fontSize: 13.5,
                                    letterSpacing: tokens.type.letterSpacing.wide,
                                    background: `${tokens.color.ink}B8`,
                                    border: `1px solid ${tokens.color.parchment}33`,
                                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                }}
                            >
                                이번 판단의 축 · {axis}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="grid gap-s" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
                {choiceMetas.map(({ choice, hint, hintTone, readability, profile }, idx) => {
                    const tone = hintTone ?? {
                            accent: tokens.color.ember,
                            border: `${tokens.color.ember}33`,
                            glow: `${tokens.color.ember}22`,
                            background: `${tokens.color.ink}66`,
                        };
                    const isHovered = hoveredChoiceId === choice.id;

                    return (
                        <motion.button
                            key={choice.id}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                delay: idx * 0.08,
                                duration: 0.4,
                                ease: tokens.easing.standard,
                            }}
                            whileHover={disabled ? undefined : { scale: 1.02, y: -2, boxShadow: `0 14px 28px ${tokens.color.ink}88, 0 0 22px ${readability.accent}44` }}
                            whileTap={disabled ? undefined : { scale: 0.98 }}
                            disabled={disabled}
                            onMouseEnter={() => handleHover(choice.id)}
                            onFocus={() => handleHover(choice.id)}
                            onMouseLeave={() => setHoveredChoiceId((current) => (current === choice.id ? null : current))}
                            onBlur={() => setHoveredChoiceId((current) => (current === choice.id ? null : current))}
                            onClick={() => handle(choice.id)}
                            className="text-left p-l rounded-m relative overflow-hidden"
                            style={{
                                background: readability.background,
                                border: `1px solid ${readability.accent}77`,
                                boxShadow: `inset 0 1px 0 ${readability.accent}33, 0 6px 14px ${tokens.color.ink}55`,
                                color: tokens.color.parchment,
                                minHeight: "clamp(220px, 24vh, 290px)",
                                cursor: disabled ? "not-allowed" : "pointer",
                                wordBreak: "keep-all",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                            }}
                        >
                            <motion.div
                                aria-hidden
                                className="pointer-events-none absolute inset-0"
                                animate={{ opacity: isHovered ? 1 : 0.54 }}
                                transition={{ duration: 0.22, ease: "easeOut" }}
                                style={{
                                    background: isHovered
                                        ? `linear-gradient(135deg, ${readability.accent}14, transparent 34%, rgba(255,255,255,0.04) 52%, transparent 72%)`
                                        : "linear-gradient(180deg, rgba(255,255,255,0.02), transparent 32%)",
                                    mixBlendMode: "screen",
                                }}
                            />
                            <motion.div
                                aria-hidden
                                className="pointer-events-none absolute inset-0"
                                animate={{
                                    opacity: isHovered ? [0.18, 0.32, 0.18] : 0.1,
                                }}
                                transition={{
                                    duration: isHovered ? 1.4 : 0.3,
                                    repeat: isHovered ? Infinity : 0,
                                    ease: "easeInOut",
                                }}
                                style={{
                                    background: `radial-gradient(circle at 84% 14%, ${readability.accent}22, transparent 28%)`,
                                    mixBlendMode: "screen",
                                }}
                            />
                            <motion.div
                                aria-hidden
                                className="pointer-events-none absolute inset-0"
                                animate={{ opacity: isHovered ? 1 : 0.8 }}
                                transition={{ duration: 0.24 }}
                                style={{
                                    background: profile.overlay,
                                    mixBlendMode: "screen",
                                }}
                            />
                            <motion.div
                                aria-hidden
                                className="pointer-events-none absolute inset-0"
                                animate={{ opacity: isHovered ? [0.18, 0.32, 0.18] : [0.08, 0.14, 0.08] }}
                                transition={{
                                    duration: isHovered ? 2.2 : 3.4,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                                style={{
                                    background: profile.veil,
                                    mixBlendMode: "screen",
                                }}
                            />
                            {profile.particles.map((particle, particleIndex) => (
                                <motion.span
                                    key={`${choice.id}-particle-${particleIndex}`}
                                    className="pointer-events-none absolute rounded-full"
                                    animate={{
                                        opacity: isHovered ? [0.14, 0.32, 0.12] : [0.06, 0.14, 0.06],
                                        y: [0, -8, -2],
                                        x: [0, particleIndex % 2 === 0 ? 3 : -2, 0],
                                    }}
                                    transition={{
                                        duration: particle.duration,
                                        delay: particle.delay,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                    }}
                                    style={{
                                        top: particle.top,
                                        left: particle.left,
                                        width: particle.size,
                                        height: particle.size,
                                        background: `${tone.accent}20`,
                                        boxShadow: `0 0 10px ${tone.accent}22`,
                                    }}
                                />
                            ))}
                            {/* 2026-04-23: 좌측 tone accent — 선택 카드도 Spirytale 언어 통일. */}
                            <div
                                aria-hidden
                                className="absolute inset-y-0 left-0"
                                style={{
                                    width: 2,
                                    background: `linear-gradient(180deg, ${readability.accent}FF 0%, ${readability.accent}33 100%)`,
                                    boxShadow: `0 0 10px ${readability.accent}AA`,
                                }}
                            />
                            <div className="pl-[6px]">
                                <div
                                    className="font-body flex items-center gap-xs mb-xs"
                                    style={{
                                        color: readability.accent,
                                        fontSize: 13.5,
                                        letterSpacing: "0.22em",
                                        opacity: 0.88,
                                        textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                    }}
                                >
                                    <span
                                        aria-hidden
                                        style={{
                                            width: 10,
                                            height: 1,
                                            background: readability.accent,
                                            boxShadow: `0 0 6px ${readability.accent}`,
                                            opacity: 0.75,
                                        }}
                                    />
                                    갈래 {idx + 1}
                                </div>
                                <div className="mb-s flex flex-wrap gap-xs">
                                    <span
                                        className="rounded-pill px-s py-[4px] font-display"
                                        style={{
                                            color: readability.accent,
                                            fontSize: 13.5,
                                            letterSpacing: tokens.type.letterSpacing.wide,
                                            background: `${readability.accent}1E`,
                                            border: `1px solid ${readability.accent}66`,
                                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                        }}
                                    >
                                        {readability.toneLabel}
                                    </span>
                                    <span
                                        className="rounded-pill px-s py-[4px] font-display"
                                        style={{
                                            color: tokens.color.parchment,
                                            fontSize: 13.5,
                                            letterSpacing: tokens.type.letterSpacing.wide,
                                            background: `${tokens.color.ink}AA`,
                                            border: `1px solid ${tokens.color.parchment}33`,
                                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                        }}
                                    >
                                        먼저 지키는 것 · {readability.focusLabel}
                                    </span>
                                    {readability.endingBadges.map((badge) => (
                                        <span
                                            key={`${choice.id}-${badge.label}-${badge.direction}`}
                                            className="rounded-pill px-s py-[4px] font-display"
                                            style={{
                                                color: badge.accent,
                                                fontSize: 13.5,
                                                letterSpacing: tokens.type.letterSpacing.wide,
                                                background: `${badge.accent}14`,
                                                border: `1px solid ${badge.accent}55`,
                                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                            }}
                                        >
                                            {badge.direction === "toward" ? "→" : "←"} {badge.label}
                                        </span>
                                    ))}
                                    <motion.span
                                        className="rounded-pill px-s py-[4px] font-display"
                                        animate={{
                                            opacity: isHovered ? 1 : 0.72,
                                            scale: isHovered ? 1.02 : 1,
                                        }}
                                        transition={{ duration: 0.18 }}
                                        style={{
                                            color: isHovered ? readability.accent : tokens.color.parchment,
                                            fontSize: 13.5,
                                            letterSpacing: tokens.type.letterSpacing.wide,
                                            background: isHovered ? `${readability.accent}14` : `${tokens.color.ink}88`,
                                            border: `1px solid ${isHovered ? `${readability.accent}55` : `${tokens.color.parchment}22`}`,
                                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                        }}
                                    >
                                        {isHovered ? profile.kicker : "판단을 고를 수 있음"}
                                    </motion.span>
                                </div>
                                <div
                                    className="font-display"
                                    style={{
                                        color: tokens.color.ember,
                                        fontSize: tokens.type.size.subtitle,
                                        letterSpacing: tokens.type.letterSpacing.normal,
                                        marginBottom: tokens.spacing.s,
                                    }}
                                >
                                    {choice.label}
                                </div>
                                {(choice.hint || choice.text) && (
                                    <div
                                        className="font-body"
                                        style={{
                                            color: tokens.color.parchment,
                                            fontSize: "clamp(22px, 1.5vw, 28px)",
                                            lineHeight: tokens.type.lineHeight.normal,
                                        }}
                                    >
                                        {choice.hint ?? choice.text}
                                    </div>
                                )}
                                <div
                                    className="font-body mt-s"
                                style={{
                                        color: tokens.color.parchment,
                                        fontSize: 15,
                                        lineHeight: tokens.type.lineHeight.normal,
                                        textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                    }}
                                >
                                    {readability.toneCopy}
                                </div>
                            </div>
                            <div className="mt-m space-y-s pl-[6px]">
                                <div
                                    className="rounded-s px-s py-s"
                                    style={{
                                        background: `linear-gradient(180deg, ${tokens.color.ink}B4, ${tokens.color.ink}8C)`,
                                        border: `1px solid ${tokens.color.brass}44`,
                                        boxShadow: `inset 0 1px 0 ${tokens.color.brass}22`,
                                    }}
                                >
                                    <div
                                        className="font-display mb-[4px]"
                                        style={{
                                            color: tokens.color.brass,
                                            fontSize: 13.5,
                                            letterSpacing: tokens.type.letterSpacing.wide,
                                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                        }}
                                    >
                                        감당해야 할 대가
                                    </div>
                                    <div
                                        className="font-body"
                                        style={{
                                            color: tokens.color.parchment,
                                            fontSize: tokens.type.size.small,
                                            lineHeight: tokens.type.lineHeight.normal,
                                        }}
                                    >
                                        {readability.riskCopy ?? "상황을 밀어붙이지 않는 대신, 다른 한쪽을 충분히 지키지 못할 수 있습니다."}
                                    </div>
                                </div>
                                {hint && (
                                    <div
                                        className="rounded-s px-s py-s"
                                        style={{
                                            background: tone.background,
                                            border: `1px solid ${tone.border}`,
                                            boxShadow: `0 0 18px ${tone.glow}`,
                                            color: tokens.color.parchment,
                                            lineHeight: tokens.type.lineHeight.normal,
                                        }}
                                    >
                                        <div
                                            className="font-display mb-[4px]"
                                            style={{
                                                color: tone.accent,
                                                fontSize: 13.5,
                                                letterSpacing: tokens.type.letterSpacing.wide,
                                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                            }}
                                        >
                                            이 선택 앞에서 떠오르는 생각
                                        </div>
                                        {hint.speaker && (
                                            <div
                                                className="font-display mb-[2px]"
                                                style={{
                                                    color: tone.accent,
                                                    fontSize: 13.5,
                                                    letterSpacing: tokens.type.letterSpacing.wide,
                                                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                                }}
                                            >
                                                {hint.speaker}
                                            </div>
                                        )}
                                        <div
                                            className="font-body"
                                            style={{
                                                fontSize: tokens.type.size.small,
                                                lineHeight: tokens.type.lineHeight.normal,
                                            }}
                                        >
                                            {hint.text}
                                        </div>
                                    </div>
                                )}
                                <div
                                    className="font-display"
                                    style={{
                                        color: disabled ? tokens.color.parchment : tokens.color.brass,
                                        fontSize: 13.5,
                                        letterSpacing: tokens.type.letterSpacing.wide,
                                        textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                    }}
                                >
                                    {isHovered ? "이 카드를 뒤집으면 바로 사건이 진행됩니다" : "선택하면 바로 이 판단으로 사건이 진행됩니다"}
                                </div>
                                <motion.div
                                    aria-hidden
                                    animate={{
                                        x: isHovered ? [0, 4, 0] : 0,
                                        opacity: isHovered ? 1 : 0.54,
                                    }}
                                    transition={{
                                        duration: 0.9,
                                        repeat: isHovered ? Infinity : 0,
                                        ease: "easeInOut",
                                    }}
                                    className="font-display mt-[6px]"
                                    style={{
                                        color: isHovered ? readability.accent : tokens.color.parchment,
                                        fontSize: 13.5,
                                        letterSpacing: tokens.type.letterSpacing.wide,
                                        textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                    }}
                                >
                                    {isHovered ? "이 선택 펼쳐 보기 ▸" : "선택을 고르는 중"}
                                </motion.div>
                            </div>
                        </motion.button>
                    );
                })}
            </div>
        </div>
    );
}

function inferChoiceReadability(
    choice: EventChoice,
    hint?: ScenarioReactiveLinePayload | null,
): ChoiceReadabilityMeta {
    const haystack = `${choice.label} ${choice.hint ?? ""} ${choice.text ?? ""} ${hint?.text ?? ""}`.toLowerCase();
    const explicitTone = (choice.tone ?? "").trim();

    if (explicitTone) {
        const normalized = explicitTone.toLowerCase();
        if (normalized.includes("결연")) {
            return {
                toneLabel: "결연한 선택",
                toneCopy: "눈앞의 손실을 감수하더라도 전열과 약속을 붙드는 쪽의 판단입니다.",
                focusLabel: "병사와 전열",
                riskCopy: "소모가 빠르게 쌓이거나, 후방 여유가 줄어들 수 있습니다.",
                accent: tokens.color.ember,
                background: "linear-gradient(160deg, rgba(201,106,59,0.16), rgba(14,12,24,0.92) 58%)",
                endingBadges: buildEndingBadges(choice.endingShift),
            };
        }
        if (normalized.includes("계산")) {
            return {
                toneLabel: "계산적인 선택",
                toneCopy: "당장의 감정보다 보급과 지속 가능성을 먼저 따져 움직이는 판단입니다.",
                focusLabel: "보급과 여력",
                riskCopy: "사람들의 마음이 식거나, 냉정하다는 인상을 남길 수 있습니다.",
                accent: tokens.color.steel,
                background: "linear-gradient(160deg, rgba(74,85,104,0.18), rgba(14,12,24,0.94) 58%)",
                endingBadges: buildEndingBadges(choice.endingShift),
            };
        }
        if (normalized.includes("온정")) {
            return {
                toneLabel: "온정 어린 선택",
                toneCopy: "눈앞의 효율보다 사람의 상처와 남을 얼굴을 먼저 살피는 판단입니다.",
                focusLabel: "주민과 추모",
                riskCopy: "속도가 늦어지거나, 병사 쪽 불만이 쌓일 수 있습니다.",
                accent: tokens.color.moss,
                background: "linear-gradient(160deg, rgba(62,90,58,0.18), rgba(14,12,24,0.94) 58%)",
                endingBadges: buildEndingBadges(choice.endingShift),
            };
        }
        if (normalized.includes("야심")) {
            return {
                toneLabel: "야심 어린 선택",
                toneCopy: "이번 국면을 크게 뒤집거나, 더 큰 결과를 가져오려는 공격적인 판단입니다.",
                focusLabel: "공세와 장악",
                riskCopy: "실패하면 되돌릴 여지가 적고, 소모와 반감이 함께 커질 수 있습니다.",
                accent: tokens.color.burgundy,
                background: "linear-gradient(160deg, rgba(92,31,46,0.22), rgba(14,12,24,0.94) 58%)",
                endingBadges: buildEndingBadges(choice.endingShift),
            };
        }
        if (normalized.includes("방관")) {
            return {
                toneLabel: "거리 두는 선택",
                toneCopy: "한걸음 물러서서 사건을 흘려보내거나, 직접 짊어지지 않으려는 판단입니다.",
                focusLabel: "당장의 손실 회피",
                riskCopy: "지금은 버틸 수 있어도, 뒤늦은 불신이나 공백이 더 크게 돌아올 수 있습니다.",
                accent: tokens.color.parchment,
                background: "linear-gradient(160deg, rgba(184,169,132,0.12), rgba(14,12,24,0.94) 58%)",
                endingBadges: buildEndingBadges(choice.endingShift),
            };
        }
    }

    if (includesChoiceKeyword(haystack, ["지켜", "버틴", "버텨", "붙든", "엄호", "방패", "막아", "hold", "banner"])) {
        return {
            toneLabel: "결연한 선택",
            toneCopy: "눈앞의 손실을 감수하더라도 전열과 약속을 붙드는 쪽의 판단입니다.",
            focusLabel: "병사와 전열",
            riskCopy: "소모가 빠르게 쌓이거나, 후방 여유가 줄어들 수 있습니다.",
            accent: tokens.color.ember,
            background: "linear-gradient(160deg, rgba(201,106,59,0.16), rgba(14,12,24,0.92) 58%)",
            endingBadges: buildEndingBadges(choice.endingShift),
        };
    }

    if (includesChoiceKeyword(haystack, ["보급", "장부", "계산", "빚", "저장", "절약", "운반", "supply", "ledger", "shortcut"])) {
        return {
            toneLabel: "계산적인 선택",
            toneCopy: "당장의 감정보다 보급과 지속 가능성을 먼저 따져 움직이는 판단입니다.",
            focusLabel: "보급과 여력",
            riskCopy: "사람들의 마음이 식거나, 냉정하다는 인상을 남길 수 있습니다.",
            accent: tokens.color.steel,
            background: "linear-gradient(160deg, rgba(74,85,104,0.18), rgba(14,12,24,0.94) 58%)",
            endingBadges: buildEndingBadges(choice.endingShift),
        };
    }

    if (includesChoiceKeyword(haystack, ["주민", "설득", "돕", "나눠", "달랜", "묻", "장례", "편지", "trust", "civilian", "memorial"])) {
        return {
            toneLabel: "온정 어린 선택",
            toneCopy: "눈앞의 효율보다 사람의 상처와 남을 얼굴을 먼저 살피는 판단입니다.",
            focusLabel: "주민과 추모",
            riskCopy: "속도가 늦어지거나, 병사 쪽 불만이 쌓일 수 있습니다.",
            accent: tokens.color.moss,
            background: "linear-gradient(160deg, rgba(62,90,58,0.18), rgba(14,12,24,0.94) 58%)",
            endingBadges: buildEndingBadges(choice.endingShift),
        };
    }

    if (includesChoiceKeyword(haystack, ["정복", "진격", "강행", "끝장", "장악", "conquest", "charge"])) {
        return {
            toneLabel: "야심 어린 선택",
            toneCopy: "이번 국면을 크게 뒤집거나, 더 큰 결과를 가져오려는 공격적인 판단입니다.",
            focusLabel: "공세와 장악",
            riskCopy: "실패하면 되돌릴 여지가 적고, 소모와 반감이 함께 커질 수 있습니다.",
            accent: tokens.color.burgundy,
            background: "linear-gradient(160deg, rgba(92,31,46,0.22), rgba(14,12,24,0.94) 58%)",
            endingBadges: buildEndingBadges(choice.endingShift),
        };
    }

    if (includesChoiceKeyword(haystack, ["정찰", "유적", "탐색", "봉인", "조사", "scout", "relic", "seal", "survey"])) {
        return {
            toneLabel: "탐색하는 선택",
            toneCopy: "당장 보이는 손익보다, 더 큰 정보와 숨은 위험을 확인하려는 판단입니다.",
            focusLabel: "정보와 징후",
            riskCopy: "얻는 것이 적게 보일 수 있고, 잘못 건드리면 새 위험이 열릴 수 있습니다.",
            accent: tokens.color.brass,
            background: "linear-gradient(160deg, rgba(169,123,61,0.18), rgba(14,12,24,0.94) 58%)",
            endingBadges: buildEndingBadges(choice.endingShift),
        };
    }

    return {
        toneLabel: "신중한 선택",
        toneCopy: "크게 흔들기보다, 지금 국면을 어떻게 받아넘길지 가늠하는 판단입니다.",
        focusLabel: "현재 국면 정리",
        riskCopy: "한쪽을 크게 살리지도, 완전히 잃지도 않는 대신 무게가 오래 남을 수 있습니다.",
        accent: tokens.color.parchment,
        background: "linear-gradient(160deg, rgba(184,169,132,0.12), rgba(14,12,24,0.94) 58%)",
        endingBadges: buildEndingBadges(choice.endingShift),
    };
}

function includesChoiceKeyword(text: string, needles: string[]) {
    return needles.some((needle) => text.includes(needle));
}

function buildChoiceSurfaceProfile(
    readability: ChoiceReadabilityMeta,
    hintTone: ChoiceHintTone | null,
): ChoiceSurfaceProfile {
    const accent = hintTone?.accent ?? readability.accent;
    const particleSet: ChoiceAmbientParticle[] = [
        { top: "14%", left: "16%", size: 6, duration: 8.4, delay: 0.2 },
        { top: "26%", left: "84%", size: 4, duration: 9.6, delay: 0.5 },
        { top: "58%", left: "76%", size: 5, duration: 10.2, delay: 0.9 },
        { top: "82%", left: "22%", size: 4, duration: 11.6, delay: 0.3 },
    ];

    if (readability.focusLabel.includes("병사") || readability.focusLabel.includes("전열")) {
        return {
            overlay: `linear-gradient(135deg, ${accent}14, transparent 28%, rgba(255,255,255,0.03) 46%, transparent 68%)`,
            veil: `radial-gradient(circle at 18% 18%, ${accent}18, transparent 28%)`,
            particles: particleSet,
            kicker: "전열의 카드가 들린다",
        };
    }
    if (readability.focusLabel.includes("보급") || readability.focusLabel.includes("여력")) {
        return {
            overlay: `linear-gradient(180deg, rgba(255,255,255,0.025), transparent 24%), linear-gradient(135deg, ${accent}12, transparent 34%)`,
            veil: `radial-gradient(circle at 82% 18%, ${accent}16, transparent 26%)`,
            particles: particleSet.map((particle, index) => ({ ...particle, delay: particle.delay + index * 0.08 })),
            kicker: "장부 위의 선이 움직인다",
        };
    }
    if (readability.focusLabel.includes("주민") || readability.focusLabel.includes("추모")) {
        return {
            overlay: `linear-gradient(180deg, ${accent}10, transparent 28%), linear-gradient(135deg, rgba(255,255,255,0.02), transparent 52%)`,
            veil: `radial-gradient(circle at 50% 16%, ${accent}14, transparent 24%)`,
            particles: particleSet.slice(0, 3),
            kicker: "남겨질 얼굴이 스친다",
        };
    }
    if (readability.focusLabel.includes("공세") || readability.focusLabel.includes("장악")) {
        return {
            overlay: `linear-gradient(135deg, ${accent}18, transparent 24%, rgba(255,255,255,0.04) 42%, transparent 64%)`,
            veil: `radial-gradient(circle at 84% 16%, ${accent}18, transparent 24%)`,
            particles: particleSet.map((particle) => ({ ...particle, duration: particle.duration - 1.4 })),
            kicker: "밀어붙일 카드가 뜬다",
        };
    }
    if (readability.focusLabel.includes("정보") || readability.focusLabel.includes("징후")) {
        return {
            overlay: `linear-gradient(135deg, ${accent}12, transparent 30%, rgba(255,255,255,0.03) 50%, transparent 72%)`,
            veil: `radial-gradient(circle at 72% 22%, ${accent}16, transparent 22%)`,
            particles: particleSet,
            kicker: "숨은 결이 보이기 시작한다",
        };
    }

    return {
        overlay: `linear-gradient(180deg, rgba(255,255,255,0.02), transparent 26%), linear-gradient(135deg, ${accent}10, transparent 34%)`,
        veil: `radial-gradient(circle at 80% 18%, ${accent}14, transparent 24%)`,
        particles: particleSet,
        kicker: "한 장의 판단이 들린다",
    };
}

function buildEndingBadges(endingShift?: Record<string, number> | null) {
    if (!endingShift) return [];

    const labels: Record<string, string> = {
        conquest: "정복",
        armistice: "화의",
        return: "귀환",
        hollow: "공허한 귀환",
        buried: "선 밖의 매장",
    };

    return Object.entries(endingShift)
        .filter(([, value]) => value !== 0)
        .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
        .slice(0, 2)
        .map(([key, value]) => ({
            label: labels[key] ?? key,
            direction: value > 0 ? "toward" as const : "away" as const,
            accent: value > 0 ? tokens.color.brass : tokens.color.steel,
        }));
}
